<script>
/**
 * @component PrescriptionQueue
 * @description Bandeja de recetas para admin/médico. Lista solicitudes pendientes
 *              con acciones de aprobar y rechazar.
 * @example <PrescriptionQueue />
 */
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import medicationRequestService from "@/services/medicationRequest";
import helperService from "@/services/helper";

export default {
  name: "PrescriptionQueue",

  components: { BaseSubNav, Spinner },

  data() {
    return {
      isLoading: false,
      medicationRequests: [],
      searchInput: "",
    };
  },

  computed: {
    /**
     * @description Solicitudes filtradas por búsqueda de texto libre
     * @returns {Array}
     */
    filteredMedicationRequests() {
      if (!this.searchInput) return this.medicationRequests;
      const query = this.searchInput.toLowerCase();
      return this.medicationRequests.filter((r) => {
        const med =
          r.medication?.name ||
          r.publicMedication?.name ||
          r.publicMedication?.brandName ||
          "";
        return (
          r.patient?.firstName?.toLowerCase().includes(query) ||
          r.patient?.lastName?.toLowerCase().includes(query) ||
          med.toLowerCase().includes(query) ||
          this.getDiagnosisName(r).toLowerCase().includes(query)
        );
      });
    },
  },

  watch: {
    "$route.name": {
      handler() {
        this.getData();
      },
      immediate: true,
    },
  },

  methods: {
    /**
     * @description Carga solicitudes pendientes desde el backend
     */
    async getData() {
      this.isLoading = true;
      try {
        const res = await medicationRequestService.get({ status: "pending" });
        this.medicationRequests = res.docs || res || [];
      } catch (error) {
        console.error("Error loading medication requests:", error);
        this.$message.error("Error al cargar las solicitudes");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Nombre completo del paciente
     * @param {Object} patient
     * @returns {string}
     */
    getFullName(patient) {
      return helperService.getFullName(patient);
    },

    /**
     * @description Nombre del medicamento de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getMedicationName(request) {
      return (
        request.medication?.name ||
        request.publicMedication?.name ||
        request.publicMedication?.brandName ||
        "Sin medicación"
      );
    },

    /**
     * @description Diagnóstico de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getDiagnosisName(request) {
      if (typeof request.diagnosis === "string") return request.diagnosis;
      if (request.diagnosis?.name) return request.diagnosis.name;
      if (typeof request.publicDiagnosis === "string")
        return request.publicDiagnosis;
      if (request.publicDiagnosis?.name) return request.publicDiagnosis.name;
      return "Sin diagnóstico";
    },

    /**
     * @description Aprueba una solicitud pendiente
     * @param {Object} request
     */
    async approveRequest(request) {
      try {
        await this.$confirm(
          "¿Desea aprobar esta solicitud de medicamento?",
          "Aprobar solicitud",
          {
            confirmButtonText: "Aprobar",
            cancelButtonText: "Cancelar",
            type: "question",
          }
        );
        await medicationRequestService.update(request._id, {
          status: "approved",
          doctor: "6500671a4704198186ef94d4",
          diagnosis: request.diagnosis,
        });
        this.medicationRequests.splice(
          this.medicationRequests.findIndex((r) => r._id === request._id),
          1
        );
        this.$message({ type: "success", message: "Solicitud aprobada" });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error approving:", error);
          this.$message({
            type: "error",
            message: "Error al aprobar la solicitud",
          });
        }
      }
    },

    /**
     * @description Rechaza una solicitud pendiente
     * @param {Object} request
     */
    async rejectRequest(request) {
      try {
        await this.$confirm(
          "¿Desea rechazar esta solicitud de medicamento?",
          "Rechazar solicitud",
          {
            confirmButtonText: "Rechazar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await medicationRequestService.update(request._id, {
          status: "rejected",
          doctor: "6500671a4704198186ef94d4",
          diagnosis: request.diagnosis,
        });
        this.medicationRequests.splice(
          this.medicationRequests.findIndex((r) => r._id === request._id),
          1
        );
        this.$message({ type: "warning", message: "Solicitud rechazada" });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error rejecting:", error);
          this.$message({
            type: "error",
            message: "Error al rechazar la solicitud",
          });
        }
      }
    },

    /**
     * @description Navega al dashboard del paciente
     * @param {Object|string} patient
     */
    goToPatientDashboard(patient) {
      const id = typeof patient === "string" ? patient : patient._id;
      this.$router.push({ name: "patient-dashboard", params: { id } });
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(navigationType="prescription" :showNavigation="true")
    template(#actions)
      .pq-search
        micon(name="search")
        input.pq-search__input(
          v-model="searchInput"
          placeholder="Buscar por paciente, medicación o diagnóstico..."
        )

  .pq-card
    //- Loading
    .pq-loading(v-if="isLoading")
      spinner

    //- Empty state
    .pq-empty(v-else-if="!filteredMedicationRequests.length")
      micon(name="inbox")
      p.pq-empty__title
        | {{ searchInput ? 'Sin resultados para "' + searchInput + '"' : 'No hay solicitudes pendientes' }}
      p.pq-empty__sub(v-if="!searchInput") Cuando un paciente solicite una receta aparecerá aquí

    //- Tabla
    table.pq-table(v-else)
      thead
        tr
          th Paciente
          th Diagnóstico
          th Medicación
          th Estado
          th Fecha de solicitud
          th Acciones
      tbody
        tr(
          v-for="request in filteredMedicationRequests"
          :key="request._id"
          @click="goToPatientDashboard(request.patient)"
        )
          td
            .pq-patient {{ getFullName(request.patient) }}
          td
            span.pq-diag {{ getDiagnosisName(request) }}
          td
            .pq-med {{ getMedicationName(request) }}
          td
            span.pq-badge.pq-badge--pending Pendiente
          td
            span.pq-date {{ request.createdAt | formatDateAndTime }}
          td.pq-actions(@click.stop)
            button.pq-action-btn.pq-action-btn--approve(
              @click="approveRequest(request)"
              title="Aprobar"
            )
              micon(name="check")
            button.pq-action-btn.pq-action-btn--reject(
              @click="rejectRequest(request)"
              title="Rechazar"
            )
              micon(name="clear")
</template>

<style lang="scss" scoped>
// ── Search ────────────────────────────────────────────
.pq-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 12px;
  width: 320px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: #1e3a5f;
    background: #fff;
  }

  .micon {
    width: 16px;
    height: 16px;
    fill: #94a3b8;
    flex-shrink: 0;
  }

  &__input {
    border: none;
    background: none;
    outline: none;
    font-size: 13px;
    color: #1e293b;
    width: 100%;
    font-family: inherit;

    &::placeholder {
      color: #94a3b8;
    }
  }
}

// ── Card ──────────────────────────────────────────────
.pq-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  margin-top: 16px;
}

// ── Loading / empty ────────────────────────────────────
.pq-loading {
  padding: 48px;
  text-align: center;
}

.pq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;

  .micon {
    width: 40px;
    height: 40px;
    fill: #e2e8f0;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
  }

  &__sub {
    font-size: 13px;
    color: #94a3b8;
  }
}

// ── Tabla ─────────────────────────────────────────────
.pq-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f8fafc;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.1s;

    &:last-child td {
      border-bottom: none;
    }
    &:hover td {
      background: #f8fafc;
    }
  }

  td.pq-actions {
    cursor: default;
    width: 90px;
  }
}

.pq-patient {
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
}

.pq-diag {
  font-size: 13px;
  color: #475569;
}

.pq-med {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
}

.pq-date {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

// Badge estado
.pq-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--pending {
    background: #fff7ed;
    color: #ea580c;
    &::before {
      background: #ea580c;
    }
  }
  &--approved {
    background: #f0fdf4;
    color: #16a34a;
    &::before {
      background: #16a34a;
    }
  }
  &--rejected {
    background: #fef2f2;
    color: #dc2626;
    &::before {
      background: #dc2626;
    }
  }
}

// Botones de acción
.pq-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.pq-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;

  .micon {
    width: 16px;
    height: 16px;
  }

  &--approve {
    background: #f0fdf4;
    border-color: #bbf7d0;
    .micon {
      fill: #16a34a;
    }
    &:hover {
      background: #16a34a;
      border-color: #16a34a;
      .micon {
        fill: #fff;
      }
    }
  }

  &--reject {
    background: #fef2f2;
    border-color: #fecaca;
    .micon {
      fill: #dc2626;
    }
    &:hover {
      background: #dc2626;
      border-color: #dc2626;
      .micon {
        fill: #fff;
      }
    }
  }
}

@media (max-width: 767px) {
  .pq-search {
    width: 200px;
  }
  .pq-table {
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(4),
    td:nth-child(4) {
      display: none;
    }
  }
}
</style>
